import { TFuncKey } from 'i18next';

import { Option } from 'types';
import { CustomerData } from 'types/api/customer.type';
import { OrderDetails } from 'types/api/orders.type';

import { formatSeasonLabel } from 'helpers/formatSeason';

export type FormFieldKind = 'select' | 'readonly';

export type FormField = {
  id: string;
  label: TFuncKey;
  name: string;
  disabled?: boolean;
  options: Option[];
  defaultValue?: string | number;
  required?: boolean;
  kind?: FormFieldKind;
  readonlyValue?: string;
};

export type OrderFormFields = Readonly<{
  customer: FormField;
  shipping: FormField;
  goods: FormField;
  /** `2027 ES - ORIGINALS` (the order's zone is not shown here). */
  seasonCollection: string;
  orderTakenOn: FormField;
  deliveryCode: FormField;
}>;

const emptySelect = (
  id: string,
  label: TFuncKey,
  name: string,
  disabled: boolean
): FormField => ({
  id,
  label,
  name,
  kind: 'select',
  disabled,
  options: [],
});

export const useFormData = (
  order?: OrderDetails,
  customerInfo?: CustomerData,
  extraFields?: {
    shippingAddressOptions: Option[];
    goodsDestinationOptions: Option[];
    deliveryCodeOptions: Option[];
    orderTakenOnOptions: Option[];
    selectedShippingAddress: string | null;
    selectedGoodsDestination: string | null;
    selectedDeliveryCode: string | null;
    selectedOrderTakenOn: string | null;
    season: string;
    collection: string;
    zone: string;
    isSent: boolean;
  }
): OrderFormFields => {
  const isSent = extraFields?.isSent ?? false;
  const hasProducts = (order?.items?.length ?? 0) >= 1;
  const customerLocked = isSent || hasProducts;

  const customer: FormField = {
    id: '2',
    label: 'customer.label',
    name: 'customerCode',
    kind: customerLocked ? 'readonly' : 'select',
    // Code only — the name is shown in the separate name box on the order page.
    readonlyValue: customerInfo?.code || undefined,
    options: [],
    disabled: customerLocked,
    defaultValue: customerInfo?.code,
  };

  const joinSeasonCollection = (season?: string, collection?: string) =>
    [season ? formatSeasonLabel(season) : season, collection]
      .filter((p) => p != null && String(p).trim() !== '')
      .join(' - ');

  if (!extraFields) {
    return {
      customer,
      shipping: emptySelect('6', 'address.shipping', 'shippingAddressCode', true),
      goods: emptySelect('7', 'address.goodsDestination', 'goodsDestinationCode', true),
      seasonCollection: '—',
      orderTakenOn: {
        id: '5',
        label: 'order.takenOn',
        name: 'orderTakenOn',
        kind: 'select',
        disabled: true,
        options: [],
        required: true,
      },
      deliveryCode: emptySelect('8', 'order.orderDeliveryCode', 'deliveryCode', true),
    };
  }

  const seasonCollection = joinSeasonCollection(extraFields.season, extraFields.collection);

  return {
    customer,
    shipping: {
      id: '6',
      label: 'address.shipping',
      name: 'shippingAddressCode',
      kind: 'select',
      disabled: isSent,
      options: extraFields.shippingAddressOptions,
      defaultValue: extraFields.selectedShippingAddress || undefined,
    },
    goods: {
      id: '7',
      label: 'address.goodsDestination',
      name: 'goodsDestinationCode',
      kind: 'select',
      disabled: isSent,
      options: extraFields.goodsDestinationOptions,
      defaultValue: extraFields.selectedGoodsDestination || undefined,
    },
    seasonCollection: seasonCollection || '—',
    orderTakenOn: {
      id: '5',
      label: 'order.takenOn',
      name: 'orderTakenOn',
      kind: 'select',
      disabled: isSent,
      options: extraFields.orderTakenOnOptions,
      defaultValue: extraFields.selectedOrderTakenOn || undefined,
      // required: true,
    },
    deliveryCode: {
      id: '8',
      label: 'order.orderDeliveryCode',
      name: 'deliveryCode',
      kind: 'select',
      disabled: isSent,
      options: extraFields.deliveryCodeOptions,
      defaultValue: extraFields.selectedDeliveryCode || undefined,
    },
  };
};
